import { FLOW_TYPES, REQUEST_STATUS, REQUEST_TYPES } from './constants';
import { formatHireSalarySummary } from './utils';
import {
  MOCK_APPROVAL_CHAINS,
  MOCK_APPROVERS_POOL,
  MOCK_APPROVAL_TEMPLATES,
  MOCK_DEPARTMENTS,
  MOCK_LIVE_JOBS,
  MOCK_PROMOTION_CHANNELS,
  MOCK_REQUESTS,
} from './service-data';

const delay = (ms = 400) => new Promise((r) => setTimeout(r, ms));

const failChance = () => {
  const flag = process.env.REACT_APP_AUTHORITY_MOCK_FAIL;
  if (!flag) return false;
  const rate = Number(flag);
  return !Number.isNaN(rate) && Math.random() < rate;
};

let requestsStore = [...MOCK_REQUESTS];
let chainsStore = [...MOCK_APPROVAL_CHAINS];

const buildRequestFromPayload = (payload) => {
  const isCampaign = payload?.requestType === REQUEST_TYPES.CAMPAIGN || payload?.campaign?.title;
  const hire = payload?.hire || {};
  const campaign = payload?.campaign || {};
  const approval = payload?.approval || {};
  const approvers = (approval.approvers || []).map((a, i) => ({
    userId: a.value,
    name: a.name,
    role: a.role,
    email: a.email,
    status: i === 0 ? 'awaiting' : 'upcoming',
    order: i + 1,
  }));

  const channelNames = (campaign.channelIds || [])
    .map((id) => MOCK_PROMOTION_CHANNELS.find((c) => c.id === id)?.name)
    .filter(Boolean);

  const base = {
    id: `req-${Date.now()}`,
    type: isCampaign ? REQUEST_TYPES.CAMPAIGN : REQUEST_TYPES.HIRE,
    status: REQUEST_STATUS.PENDING,
    flowType: approval.flowType,
    note: isCampaign ? campaign.reason : hire.reason,
    reason: isCampaign ? campaign.reason : hire.reason,
    justification: isCampaign ? campaign.justification : hire.justification,
    requestedBy: { id: 'current-user', name: 'You' },
    createdAt: new Date().toISOString(),
    approvedCount: 0,
    totalApprovers: approvers.length,
    approvers,
  };

  if (isCampaign) {
    return {
      ...base,
      title: campaign.title,
      jobTitle:
        campaign.job?.label?.split(' — ')[0] ||
        MOCK_LIVE_JOBS.find((j) => j.value === campaign.job?.value)?.label?.split(' — ')[0],
      budget: campaign.budget,
      durationDays: campaign.durationDays,
      channels: channelNames,
    };
  }

  return {
    ...base,
    title: hire.jobTitle,
    department: MOCK_DEPARTMENTS.find((d) => d.value === hire.department)?.label || hire.department,
    location: hire.location,
    positions: hire.positions,
    salaryRange: formatHireSalarySummary(hire),
  };
};

export const fetchAuthorityRequests = async (params = {}) => {
  await delay();
  if (failChance()) {
    throw new Error('Unable to load approval requests.');
  }
  let requests = [...requestsStore];
  if (params?.type && params.type !== 'all') {
    requests = requests.filter((r) => r.type === params.type);
  }
  return {
    requests,
    totalCount: requests.length,
  };
};

export const fetchApprovalChains = async (params = {}) => {
  await delay(300);
  if (failChance()) {
    throw new Error('Unable to load approval chains.');
  }
  let chains = [...chainsStore];
  if (params?.type && params.type !== 'all') {
    chains = chains.filter((c) => c.type === params.type);
  }
  return {
    chains,
    totalCount: chains.length,
  };
};

export const fetchAuthorityReferenceData = async () => {
  await delay(250);
  if (failChance()) {
    throw new Error('Unable to load reference data.');
  }
  return {
    departments: MOCK_DEPARTMENTS,
    liveJobs: MOCK_LIVE_JOBS,
    templates: MOCK_APPROVAL_TEMPLATES,
    approversPool: MOCK_APPROVERS_POOL,
    promotionChannels: MOCK_PROMOTION_CHANNELS,
  };
};

/** @deprecated Use RTK queries — kept for backwards compatibility */
export const fetchAuthorityToRecruitData = async () => {
  const [requestsRes, chainsRes, reference] = await Promise.all([
    fetchAuthorityRequests(),
    fetchApprovalChains(),
    fetchAuthorityReferenceData(),
  ]);
  return {
    requests: requestsRes.requests,
    chains: chainsRes.chains,
    ...reference,
  };
};

export const createApprovalChain = async (payload) => {
  await delay(500);
  if (failChance()) {
    throw new Error('Could not create approval chain. Please try again.');
  }

  const approvers = (payload.approvers || []).map((a) => ({
    name: a.name,
    role: a.role,
  }));

  const chain = {
    id: `chain-${Date.now()}`,
    type: payload.type,
    title: payload.name?.trim(),
    description: payload.description?.trim() || '',
    flowType: payload.flowType,
    approvers,
    rolesSummary: approvers
      .map((a) => a.role)
      .join(payload.flowType === FLOW_TYPES.PARALLEL ? ' + ' : ' → '),
  };

  chainsStore = [...chainsStore, chain];
  return {
    success: true,
    message: 'Approval chain created.',
    chain,
  };
};

export const updateApprovalChain = async (payload) => {
  await delay(500);
  if (failChance()) {
    throw new Error('Could not update approval chain. Please try again.');
  }

  const index = chainsStore.findIndex((c) => c.id === payload.id);
  if (index === -1) {
    throw new Error('Approval chain not found.');
  }

  const approvers = (payload.approvers || []).map((a) => ({
    name: a.name,
    role: a.role,
    value: a.value,
    userId: a.userId,
    email: a.email,
  }));

  const updated = {
    ...chainsStore[index],
    type: payload.type,
    title: payload.name?.trim(),
    description: payload.description?.trim() || '',
    flowType: payload.flowType,
    approvers,
    rolesSummary: approvers
      .map((a) => a.role)
      .join(payload.flowType === FLOW_TYPES.PARALLEL ? ' + ' : ' → '),
  };

  chainsStore = chainsStore.map((c) => (c.id === payload.id ? updated : c));

  return {
    success: true,
    message: 'Approval chain updated.',
    chain: updated,
  };
};

export const deleteApprovalChain = async (chainId) => {
  await delay(400);
  if (failChance()) {
    throw new Error('Could not delete approval chain. Please try again.');
  }

  const exists = chainsStore.some((c) => c.id === chainId);
  if (!exists) {
    throw new Error('Approval chain not found.');
  }

  chainsStore = chainsStore.filter((c) => c.id !== chainId);

  return {
    success: true,
    message: 'Approval chain deleted.',
    id: chainId,
  };
};

export const createAuthorityRequest = async (payload) => {
  await delay(600);
  if (failChance()) {
    throw new Error('Submission failed. Please try again.');
  }
  const request = buildRequestFromPayload(payload);
  requestsStore = [request, ...requestsStore];
  return {
    success: true,
    message: 'Request submitted for approval.',
    id: request.id,
    request,
  };
};

/** @deprecated Use createAuthorityRequest */
export const submitApprovalRequest = createAuthorityRequest;

export const withdrawAuthorityRequest = async (requestId) => {
  await delay(500);
  if (!requestId) {
    throw new Error('Request not found.');
  }
  if (failChance()) {
    throw new Error('Withdrawal failed. Please try again.');
  }
  requestsStore = requestsStore.map((r) =>
    r.id === requestId ? { ...r, status: REQUEST_STATUS.WITHDRAWN } : r,
  );
  return {
    success: true,
    message: 'Request withdrawn successfully.',
    id: requestId,
  };
};
